import { useCallback, useEffect, useRef, useState } from "react";
import {
  sendMessage as sendMessageApi,
  getConversationMessages,
} from "../../../../../services/conversations";
import {
  buildApiUrl,
  getAuthHeaders,
  API_CONFIG,
} from "../../../../../services/apiConfig";

const CONCIERGE_AGENT_UUID = process.env.REACT_APP_REASONING_AGENT_UUID;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_WAIT_MS = 60000;
const MESSAGE_STATUS_POLL_MS = 1500;

/**
 * Extrae el contenido de texto de un mensaje de la API (mismo mapeo que useHomeChatMessages).
 */
function getMessageContent(msg) {
  return (
    msg.formattedText ??
    msg.messageText ??
    msg.message ??
    msg.content ??
    msg.text ??
    msg.body ??
    ""
  );
}

/**
 * Indica si el mensaje es del asistente (AGENT / assistant / OUTBOUND).
 */
function isAssistantMessage(msg) {
  const senderRole = (msg.senderRole || msg.role || "").toUpperCase();
  const direction = (msg.direction || "").toUpperCase();
  return (
    senderRole === "AGENT" ||
    msg.role === "assistant" ||
    direction === "OUTBOUND"
  );
}

/**
 * Extrae el ID de conversación de la respuesta del send (múltiples formatos posibles del backend).
 */
function getConversationIdFromSendResponse(sendResponse) {
  if (!sendResponse || typeof sendResponse !== "object") return null;
  const body = sendResponse.body;
  const data = sendResponse.data;
  return (
    sendResponse.conversationUuid ??
    sendResponse.conversation_uuid ??
    sendResponse.conversationId ??
    (body && typeof body === "object" ? body.conversationUuid ?? body.conversation_uuid ?? body.conversationId : null) ??
    (data && typeof data === "object" ? data.conversationUuid ?? data.conversation_uuid ?? data.conversationId : null)
  );
}

/**
 * Extrae request_id de la respuesta del send (para polling por message/status).
 */
function getRequestIdFromSendResponse(sendResponse) {
  if (!sendResponse || typeof sendResponse !== "object") return null;
  const body = sendResponse.body;
  const data = sendResponse.data;
  return (
    sendResponse.request_id ??
    sendResponse.requestId ??
    (body && typeof body === "object" ? body.request_id ?? body.requestId : null) ??
    (data && typeof data === "object" ? data.request_id ?? data.requestId : null)
  );
}

/**
 * Polling a /message/status/{requestId} hasta completed; devuelve el contenido del mensaje del asistente.
 */
async function pollMessageStatusForContent(requestId) {
  const url = buildApiUrl(`${API_CONFIG.endpoints.messageStatus}/${requestId}`);
  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_MAX_WAIT_MS) {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
      signal: AbortSignal.timeout(API_CONFIG.timeouts?.response ?? 30000),
    });
    if (!response.ok) {
      await new Promise((r) => setTimeout(r, MESSAGE_STATUS_POLL_MS));
      continue;
    }
    const data = await response.json();
    const status = data?.status ?? data?.body?.status;
    if (status === "completed" || status === "done") {
      const content =
        data?.body?.messageText ??
        data?.body?.content ??
        data?.body?.message ??
        data?.content ??
        data?.messageText ??
        data?.message ??
        (data?.body && typeof data.body === "string" ? data.body : null);
      if (content && String(content).trim()) {
        return String(content).trim();
      }
    }
    if (status === "error" || status === "failed") {
      throw new Error(data?.body?.message ?? data?.message ?? "Message status error");
    }
    await new Promise((r) => setTimeout(r, MESSAGE_STATUS_POLL_MS));
  }
  return null;
}

/**
 * Hook para pedirle al agente concierge que reescriba el documento
 * usando la API de chat normal (send-asynchronous + list messages).
 * Reemplaza el contenido cuando llega la respuesta.
 *
 * No muestra UI propia; expone solo estado de carga y una función rewrite.
 */
export const useDocumentRewrite = ({
  getContent,
  onReplaceContent,
  getUserId,
} = {}) => {
  const [isRewriting, setIsRewriting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const rewrite = useCallback(
    async (instruction) => {
      if (!CONCIERGE_AGENT_UUID) {
        throw new Error(
          "REACT_APP_CONCIERGE_AGENT_UUID is not configured for document rewrite"
        );
      }

      if (!instruction || !instruction.trim()) {
        // eslint-disable-next-line no-console
        console.warn("useDocumentRewrite :: empty instruction, skipping");
        return;
      }

      const baseInstruction = instruction.trim();
      const rawContent = getContent?.() ?? "";
      const content =
        typeof rawContent === "string" ? rawContent : String(rawContent || "");

      if (!content || !content.trim()) {
        // eslint-disable-next-line no-console
        console.warn("useDocumentRewrite :: empty content, skipping");
        return;
      }

      const fullMessage = `${baseInstruction}\n\nDocument to edit:\n${content}`;

      setIsRewriting(true);
      try {
        const sendResponse = await sendMessageApi({
          messageText: fullMessage,
          conversationUuid: null,
          agentUuid: CONCIERGE_AGENT_UUID,
          channel: "WEB",
        });

        const conversationUuid = getConversationIdFromSendResponse(sendResponse);
        const requestId = getRequestIdFromSendResponse(sendResponse);

        let finalContent = "";

        if (conversationUuid) {
          const startedAt = Date.now();
          while (Date.now() - startedAt < POLL_MAX_WAIT_MS) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

            const listResponse = await getConversationMessages({
              conversationId: conversationUuid,
            });
            const list = Array.isArray(listResponse.body)
              ? listResponse.body
              : Array.isArray(listResponse.messages)
                ? listResponse.messages
                : Array.isArray(listResponse.data)
                  ? listResponse.data
                  : [];

            const sorted = [...list].sort(
              (a, b) =>
                new Date(a.createdAt || a.timestamp || 0).getTime() -
                new Date(b.createdAt || b.timestamp || 0).getTime()
            );
            const assistantMsg = sorted.filter(isAssistantMessage).pop();
            if (assistantMsg) {
              const text = getMessageContent(assistantMsg);
              if (text && String(text).trim()) {
                finalContent = String(text).trim();
                break;
              }
            }
          }
        } else if (requestId) {
          finalContent = await pollMessageStatusForContent(requestId);
        }

        if (!finalContent) {
          if (!conversationUuid && !requestId) {
            // eslint-disable-next-line no-console
            console.warn("useDocumentRewrite :: send response shape", sendResponse);
            throw new Error(
              "Backend did not return conversation ID or request_id; cannot retrieve assistant response. Check console for actual response shape."
            );
          }
          throw new Error(
            "No response from concierge agent within timeout or empty content"
          );
        }

        onReplaceContent?.(finalContent);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("useDocumentRewrite :: rewrite error", error);
        throw error;
      } finally {
        if (isMountedRef.current) setIsRewriting(false);
      }
    },
    [getContent, onReplaceContent]
  );

  return {
    rewrite,
    isRewriting,
  };
};
